<script setup lang="ts">
import type { ButtonColor, ButtonVariant, ComponentSize } from '@/types/ui';
import { Icon } from '@iconify/vue';
/**
 * UiButton - Design System Button Component
 *
 * A versatile button component with multiple variants, colors, and sizes.
 *
 * VARIANT RULES (visual hierarchy):
 * - solid: Primary actions (submit, save, confirm) - main CTA, one per section
 * - outline: Secondary actions (cancel, back, alternative options)
 * - ghost: Tertiary actions (links, toolbar buttons, subtle actions)
 *
 * COLOR RULES (semantic meaning):
 * - primary: Default for most actions (submit, save, continue, confirm)
 * - dark: Neutral emphasis, professional look (settings, preferences)
 * - secondary: Alternative primary actions, less emphasis
 * - danger: Destructive actions (delete, remove, cancel subscription)
 * - white: On dark backgrounds only
 * - gradient: Special emphasis, marketing CTAs, premium features
 *
 * COMMON PATTERNS:
 * - Form submit: <UiButton>Save</UiButton> (solid primary)
 * - Form cancel: <UiButton variant="outline">Cancel</UiButton>
 * - Delete: <UiButton color="danger">Delete</UiButton>
 * - Modal confirm: solid primary + outline secondary
 *
 * @example
 * <UiButton>Save Changes</UiButton>
 * <UiButton variant="outline">Cancel</UiButton>
 * <UiButton color="danger">Delete Account</UiButton>
 */
import { computed } from 'vue';
import { IconLib } from '@/assets/icon-library';

export interface UiButtonProps {
   /** HTML button type */
   type?: 'button' | 'submit' | 'reset';
   /** Visual variant */
   variant?: ButtonVariant;
   /** Color scheme */
   color?: ButtonColor;
   /** Size variant */
   size?: Extract<ComponentSize, 'xs' | 'sm' | 'md' | 'lg'>;
   /** Horizontal padding stretch */
   stretch?: 'sm' | 'md' | 'lg';
   /** Border radius style */
   rounded?: 'full' | 'lg' | 'md';
   /** Disable interaction */
   disabled?: boolean;
   /** Show loading state */
   loading?: boolean;
   /** Expand to full width */
   fullWidth?: boolean;
   /** Square shape for icon-only buttons */
   square?: boolean;
   /** Icon to display on the left */
   leftIcon?: string;
   /** Icon to display on the right */
   rightIcon?: string;
   /** Accessible label for icon-only buttons */
   ariaLabel?: string;
}

const props = withDefaults(defineProps<UiButtonProps>(), {
   type: 'button',
   variant: 'solid',
   color: 'primary',
   size: 'sm',
   stretch: 'md',
   rounded: 'full',
   disabled: false,
   loading: false,
   fullWidth: false,
   square: false,
});

const emit = defineEmits<{
   click: [event: MouseEvent];
}>();

const buttonClasses = computed(() => {
   const baseClasses = [
      'inline-flex items-center justify-center gap-2',
      'transition-colors duration-200',
      'disabled:cursor-not-allowed',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary',
   ];

   // Size classes
   const sizeClasses = {
      xs: 'min-h-6 py-1 text-xs',
      sm: 'min-h-10 py-1.5 text-base',
      md: 'min-h-12 py-2 text-base',
      lg: 'min-h-14 py-3 text-lg',
   };

   // Horizontal padding based on stretch
   const stretchPadding = {
      xs: { sm: 'px-2', md: 'px-4', lg: 'px-6' },
      sm: { sm: 'px-3', md: 'px-5', lg: 'px-7' },
      md: { sm: 'px-5', md: 'px-7', lg: 'px-9' },
      lg: { sm: 'px-7', md: 'px-9', lg: 'px-11' },
   };

   // Square button classes
   const squareSizeClasses = {
      xs: 'size-6',
      sm: 'size-10',
      md: 'size-12',
      lg: 'size-14',
   };

   // Border radius
   const roundedClasses = {
      full: 'rounded-full',
      lg: 'rounded-lg',
      md: 'rounded-md',
   };

   // Inline gets compact fixed sizing; others use size/square/stretch
   if (props.variant === 'inline') {
      baseClasses.push('px-3 py-1 text-xs font-medium rounded-full');
   }
   else if (props.square) {
      baseClasses.push(squareSizeClasses[props.size]);
      baseClasses.push('rounded-lg');
   }
   else {
      baseClasses.push(sizeClasses[props.size]);
      baseClasses.push(stretchPadding[props.size][props.stretch]);
      baseClasses.push(roundedClasses[props.rounded]);
   }

   if (props.fullWidth) {
      baseClasses.push('w-full');
   }

   if (props.loading) {
      baseClasses.push('relative opacity-80 cursor-wait');
   }

   // Border for outline variant
   if (props.variant === 'outline') {
      baseClasses.push(props.size === 'xs' ? 'border' : 'border-2');
   }

   // Font weight (inline already set font-medium in the sizing block above)
   if (props.variant !== 'inline') {
      baseClasses.push(props.size === 'xs' ? 'font-normal' : 'font-semibold');
   }

   // Disabled treatment per variant family.
   // Solid/inline buttons swap to neutral-light fill so the disabled state
   // reads as "not available" rather than a desaturated brand color.
   // Outline/ghost stay transparent and lean on opacity, since their
   // disabled-fill equivalent would be invisible.
   const FILLED_DISABLED = 'disabled:bg-neutral-light disabled:text-black/40 disabled:hover:bg-neutral-light';
   const TRANSPARENT_DISABLED = 'disabled:opacity-50';

   // Variant + color classes
   const variantColorClasses: Record<ButtonVariant, Record<ButtonColor, string>> = {
      solid: {
         primary: `bg-primary text-white hover:bg-primary-dark ${FILLED_DISABLED}`,
         secondary: `bg-secondary text-white hover:bg-secondary-dark ${FILLED_DISABLED}`,
         dark: `bg-black text-white hover:bg-black/75 ${FILLED_DISABLED}`,
         danger: `bg-red-500 text-white hover:bg-red-600 ${FILLED_DISABLED}`,
         white: `bg-white text-black hover:bg-black/10 ${FILLED_DISABLED}`,
         // Gradient solid: filled fill when disabled (template suppresses the
         // gradient layers via `useFilledDisabledTreatment` so bg-neutral-light
         // shows through).
         gradient: `relative group ${FILLED_DISABLED}`,
         luma: `bg-primary-dark text-white hover:bg-primary ${FILLED_DISABLED}`,
      },
      outline: {
         primary: `bg-transparent border-primary text-primary hover:bg-primary/10 ${TRANSPARENT_DISABLED}`,
         secondary: `bg-transparent border-secondary text-secondary hover:bg-secondary/10 ${TRANSPARENT_DISABLED}`,
         dark: `bg-transparent border-black text-black hover:bg-black/10 ${TRANSPARENT_DISABLED}`,
         danger: `bg-transparent border-red-500 text-red-500 hover:bg-red-500/10 ${TRANSPARENT_DISABLED}`,
         white: `bg-transparent border-neutral text-black hover:bg-black/10 ${TRANSPARENT_DISABLED}`,
         gradient: `relative group ${TRANSPARENT_DISABLED}`,
         luma: `bg-transparent border-primary-light text-primary-light hover:bg-primary-light/10 ${TRANSPARENT_DISABLED}`,
      },
      ghost: {
         primary: `bg-transparent text-primary hover:bg-primary/10 ${TRANSPARENT_DISABLED}`,
         secondary: `bg-transparent text-secondary hover:bg-secondary/10 ${TRANSPARENT_DISABLED}`,
         dark: `bg-transparent text-black hover:bg-black/10 ${TRANSPARENT_DISABLED}`,
         danger: `bg-transparent text-red-500 hover:bg-red-500/10 ${TRANSPARENT_DISABLED}`,
         white: `bg-transparent text-black hover:bg-black/10 ${TRANSPARENT_DISABLED}`,
         gradient: `relative group ${TRANSPARENT_DISABLED}`,
         luma: `bg-transparent text-primary-light hover:bg-primary-light/10 ${TRANSPARENT_DISABLED}`,
      },
      inline: {
         primary: `bg-primary text-white hover:bg-primary-dark ${FILLED_DISABLED}`,
         secondary: `bg-secondary text-white hover:bg-secondary-dark ${FILLED_DISABLED}`,
         dark: `bg-black text-white hover:bg-black/75 ${FILLED_DISABLED}`,
         danger: `bg-red-500 text-white hover:bg-red-600 ${FILLED_DISABLED}`,
         white: `bg-white text-black hover:bg-black/10 ${FILLED_DISABLED}`,
         // See note on solid.gradient — same template suppression applies.
         gradient: `relative group ${FILLED_DISABLED}`,
         luma: `bg-primary-dark text-white hover:bg-primary ${FILLED_DISABLED}`,
      },
   };

   baseClasses.push(variantColorClasses[props.variant][props.color]);

   return baseClasses.join(' ');
});

const iconSize = computed(() => {
   if (props.variant === 'inline')
      return 'size-3';
   const sizes = { xs: 'size-3', sm: 'size-4', md: 'size-5', lg: 'size-6' };
   return sizes[props.size];
});

// Solid/inline gradient buttons honor the same FILLED disabled contract as
// the rest of the solid/inline variants. When this flag is true, the
// gradient layers are suppressed in the template so `bg-neutral-light` from
// `FILLED_DISABLED` shows through and the content span falls back to the
// outer button's muted text color. Outline/ghost gradient still uses
// `TRANSPARENT_DISABLED` (opacity-50) because its multi-layer rendering
// includes a transparent inner span that doesn't compose with a fill.
const useFilledDisabledTreatment = computed(
   () => props.disabled && (props.variant === 'solid' || props.variant === 'inline'),
);

function handleClick(event: MouseEvent) {
   if (!props.disabled && !props.loading) {
      emit('click', event);
   }
}
</script>

<template>
   <button
      :type="type"
      :disabled="disabled || loading"
      :class="buttonClasses"
      :aria-label="ariaLabel"
      :aria-busy="loading"
      @click="handleClick"
   >
      <!-- Gradient background (special handling). Suppressed when the
           solid/inline disabled contract is active so the FILLED treatment
           on the outer button surface is visible. -->
      <template v-if="color === 'gradient' && !useFilledDisabledTreatment">
         <span
            v-if="variant === 'outline'"
            class="absolute inset-0 bg-gradient-primary"
            :class="rounded === 'full' ? 'rounded-full' : rounded === 'lg' ? 'rounded-lg' : 'rounded-md'"
         />
         <span
            class="absolute inset-[2px] transition-opacity duration-200"
            :class="[
               rounded === 'full' ? 'rounded-full' : rounded === 'lg' ? 'rounded-lg' : 'rounded-md',
               variant === 'solid'
                  ? 'bg-gradient-primary group-hover:brightness-75'
                  : 'bg-white group-hover:opacity-0',
            ]"
         />
         <span
            class="relative flex items-center justify-center gap-2 z-10"
            :class="
               variant === 'solid'
                  ? 'text-white'
                  : 'bg-gradient-primary bg-clip-text text-transparent group-hover:text-white'
            "
         >
            <Icon
               v-if="loading"
               :icon="IconLib.loading.spinner"
               :class="iconSize"
               class="animate-spin"
            />
            <template v-else>
               <Icon v-if="leftIcon" :icon="leftIcon" :class="iconSize" />
               <slot />
               <Icon v-if="rightIcon" :icon="rightIcon" :class="iconSize" />
            </template>
         </span>
      </template>

      <!-- Regular button content -->
      <template v-else>
         <Icon
            v-if="loading"
            :icon="IconLib.loading.spinner"
            :class="iconSize"
            class="animate-spin"
         />
         <template v-else>
            <Icon v-if="leftIcon" :icon="leftIcon" :class="iconSize" />
            <slot />
            <Icon v-if="rightIcon" :icon="rightIcon" :class="iconSize" />
         </template>
      </template>
   </button>
</template>
